import { Injectable, NgZone } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private readonly consentStorageKey = 'analytics_consent';
  private initialized = false;
  private enabled = false;

  constructor(private zone: NgZone) {
    // Session replay (rrweb) binds high frequency mousemove, scroll and mutation listeners.
    // Initializing inside the Angular zone makes every one of those events run change detection.
    this.zone.runOutsideAngular(() => this.initialize());
  }

  private initialize() {
    const config = (environment as any).posthog;
    const apiKey = config?.apiKey;
    this.enabled = !!config?.enabled && !!apiKey && !apiKey.startsWith('YOUR_POSTHOG_') && typeof window !== 'undefined';
    this.updateDebugStatus('init_start');

    if (!this.enabled) {
      this.updateDebugStatus('disabled');
      return;
    }

    posthog.init(apiKey, {
      api_host: config.apiHost || 'https://eu.i.posthog.com',
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage',
      autocapture: true,
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: { password: true, email: false }
      }
    });

    this.initialized = true;
    posthog.opt_in_capturing();
    this.capture('posthog_initialized', {
      app_environment: (environment as any).environment || 'unknown'
    });
    this.updateDebugStatus('initialized');
  }

  setAnalyticsConsent(_granted: boolean) {
    try {
      localStorage.setItem(this.consentStorageKey, 'true');
    } catch {}

    if (!this.enabled || !this.initialized) {
      return;
    }

    posthog.opt_in_capturing();
  }

  capture(eventName: string, properties: any = {}) {
    if (!this.enabled || !this.initialized) {
      this.updateDebugStatus('capture_skipped');
      return;
    }

    this.zone.runOutsideAngular(() => posthog.capture(eventName, properties));
    this.updateDebugStatus('capture_sent', { eventName });
  }

  identify(userId: string) {
    if (!this.enabled || !this.initialized) {
      return;
    }

    posthog.identify(userId);
    this.updateDebugStatus('identified', { userId });
  }

  setUserProperty(name: string, value: string) {
    if (!this.enabled || !this.initialized) {
      return;
    }

    posthog.register({ [name]: value });
    this.updateDebugStatus('property_set', { name });
  }

  registerSuperProperties(properties: { [key: string]: any }) {
    if (!this.enabled || !this.initialized || !properties) {
      return;
    }

    const compact: { [key: string]: any } = {};
    Object.keys(properties).forEach((key) => {
      const value = properties[key];
      if (value !== undefined && value !== null && value !== '') {
        compact[key] = value;
      }
    });

    if (Object.keys(compact).length === 0) {
      return;
    }

    posthog.register(compact);
    this.updateDebugStatus('super_properties_set', { keys: Object.keys(compact).join(',') });
  }

  setScreenName(screenName: string) {
    this.capture('screen_view', { screen_name: screenName });
  }

  reset() {
    if (!this.enabled || !this.initialized) {
      return;
    }

    posthog.reset();
    posthog.opt_in_capturing();
    this.updateDebugStatus('reset');
  }

  triggerDebugEvent() {
    this.capture('posthog_manual_test', {
      timestamp: Date.now(),
      app_environment: (environment as any).environment || 'unknown'
    });
  }

  private updateDebugStatus(state: string, extra: any = {}) {
    if (typeof window === 'undefined') {
      return;
    }
    (window as any).__monaPosthog = {
      state,
      enabled: this.enabled,
      initialized: this.initialized,
      apiHost: ((environment as any).posthog || {}).apiHost || '',
      environment: (environment as any).environment || 'unknown',
      ...extra
    };
  }
}
